import React from 'react';
import { motion } from 'framer-motion';
import { LightbulbIcon } from 'lucide-react';
import { reportRows, recommendations, CTA_LABEL } from '../data/content';
import { Eyebrow, Heading } from './Primitives';
import { CheckoutButton } from './CheckoutButton';
const toneStyles: Record<string, string> = {
  good: 'bg-safe/12 text-safe',
  ok: 'bg-teal-50 text-teal-700',
  watch: 'bg-caution/12 text-caution'
};
function ScoreDial({
  score
}: {
  score: number;
}) {
  const radius = 52;
  const circumference = 2 * Math.PI * radius;
  return <div className="relative h-32 w-32 shrink-0 sm:h-36 sm:w-36" data-id="element-104" data-mp="1y8tiyhuivw2a">
      <svg viewBox="0 0 120 120" className="h-full w-full -rotate-90" role="img" aria-label={`Overall nutrition score ${score} out of 100`} data-id="element-105" data-mp="1tai8c4welhj0">
        <circle cx="60" cy="60" r={radius} fill="none" stroke="#FFE4CC" strokeWidth="12" data-id="element-106" data-mp="3np2mrzu4sgty" />
        <motion.circle cx="60" cy="60" r={radius} fill="none" stroke="#22A567" strokeWidth="12" strokeLinecap="round" strokeDasharray={circumference} initial={{
        strokeDashoffset: circumference
      }} whileInView={{
        strokeDashoffset: circumference * (1 - score / 100)
      }} viewport={{
        once: true,
        amount: 0.6
      }} transition={{
        duration: 1.1,
        ease: 'easeOut'
      }} data-id="element-107" data-mp="3iq83yxw5ys9u" />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center" data-id="element-108" data-mp="2j10zcqnd55ov">
        <span className="font-display text-4xl font-extrabold text-ink-900" data-id="element-109" data-mp="2j33wrvqui13m">{score}</span>
        <span className="font-mono text-[0.6rem] font-semibold uppercase tracking-[0.14em] text-ink-400" data-id="element-110" data-mp="2nzaz7ek18ugt">of 100</span>
      </div>
    </div>;
}
export function NutritionCheck() {
  return <section id="nutrition-check" aria-label="Pawsy Nutrition Check" className="relative overflow-hidden bg-orangey-900 px-5 py-12 sm:px-8 sm:py-14" data-id="element-111" data-mp="2e8g91z3o1aq3">
      <div aria-hidden="true" className="pointer-events-none absolute -right-32 top-0 h-80 w-80 rounded-full bg-orangey-700/50 blur-3xl" data-id="element-112" data-mp="36rn5udhpyhwr" />

      <div className="relative mx-auto w-full max-w-4xl" data-id="element-113" data-mp="3bnhfz23fhfdh">
        <div className="mx-auto max-w-2xl text-center" data-id="element-114" data-mp="3bo0su4e81dkn">
          <Eyebrow tone="onDark" data-id="element-115" data-mp="36nv2qsnlnz4o">The moment it all comes together</Eyebrow>
          <Heading size="lg" className="!text-white" data-id="element-116" data-mp="32mt52g9bzv4t">
            Know where your dog's nutrition stands.
          </Heading>
          <p className="mt-4 text-lg leading-relaxed text-orangey-100" data-id="element-117" data-mp="2xnzoug2ntq0y">
            Answer a few questions and Pawsy creates a personalized Nutrition Check covering feeding, hydration, treats
            and weight — with clear recommendations you can actually use.
          </p>
        </div>

        <motion.div initial={{
        opacity: 0,
        y: 26
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.15
      }} transition={{
        duration: 0.55,
        ease: 'easeOut'
      }} className="mx-auto mt-8 overflow-hidden rounded-3xl bg-white shadow-card" data-id="element-118" data-mp="2izbw0hk77uep">
          <div className="flex items-center justify-between gap-4 border-b border-orangey-100 bg-cream px-5 py-4 sm:px-7" data-id="element-119" data-mp="pgw5getif5yu">
            <div data-id="element-120" data-mp="udmkqzxhpxj7">
              <p className="font-display text-lg font-extrabold text-ink-900" data-id="element-121" data-mp="uhahe7y8f025">Pawsy Nutrition Check</p>
              <p className="font-mono text-[0.65rem] font-semibold uppercase tracking-[0.12em] text-ink-400" data-id="element-122" data-mp="yeokriz8edm8">
                4 yrs · 18.4 kg · moderately active
              </p>
            </div>
            <span className="rounded-xl bg-safe/12 px-3 py-1 font-mono text-[0.62rem] font-semibold uppercase tracking-[0.14em] text-safe" data-id="element-123" data-mp="6nijv0bosbvw">
              Today
            </span>
          </div>

          <div className="flex flex-col items-center gap-5 border-b border-orangey-100 px-5 py-7 text-center sm:flex-row sm:px-7 sm:text-left" data-id="element-124" data-mp="fo6x3st9a87q">
            <ScoreDial score={86} data-id="element-125" data-mp="3j7jrfizy7fec" />
            <div data-id="element-126" data-mp="3j6h0jb2mlcyf">
              <p className="font-mono text-[0.65rem] font-semibold uppercase tracking-[0.14em] text-orangey-700" data-id="element-127" data-mp="3j4xl1mosn5xp">
                🟢 Overall Nutrition Score
              </p>
              <p className="mt-2 font-display text-2xl font-extrabold text-ink-900" data-id="element-128" data-mp="1uo5q2x8xnh89">Your dog is in great shape.</p>
              <p className="mt-2 leading-relaxed text-ink-500" data-id="element-129" data-mp="1pq48kgr5npyw">
                Calories and portions are dialled in. Two small tweaks below would push this into the 90s.
              </p>
            </div>
          </div>

          <ul className="divide-y divide-orangey-100" data-id="element-130" data-mp="1yn4xcoi4zq2v">
            {reportRows.map(row => <li key={row.label} className="flex items-center gap-3.5 px-5 py-4 sm:px-7" data-id="element-131" data-mp="2dfovfaqfj7dg">
                <span aria-hidden="true" className="text-2xl" data-id="element-132" data-mp="28gvj3lmvpmev">
                  {row.emoji}
                </span>
                <div className="min-w-0 flex-1" data-id="element-133" data-mp="2icyqb5wl8b8o">
                  <p className="font-display text-base font-bold text-ink-900" data-id="element-134" data-mp="2ie9uv7xuxn0u">{row.label}</p>
                  <p className="truncate text-sm text-ink-400" data-id="element-135" data-mp="s1amofi5s3r4">{row.detail}</p>
                </div>
                <span className={`shrink-0 rounded-xl px-2.5 py-1 font-mono text-[0.62rem] font-semibold uppercase tracking-[0.1em] ${toneStyles[row.tone]}`} data-id="element-136" data-mp="o08x6qe1n62c">
                  {row.status}
                </span>
              </li>)}
          </ul>

          <div className="bg-cream px-5 py-6 sm:px-7" data-id="element-137" data-mp="444j9d9i59o3">
            <p className="flex items-center gap-2 font-display text-base font-extrabold text-ink-900" data-id="element-138" data-mp="94ahjcaa1uf5">
              <LightbulbIcon className="h-5 w-5 text-orangey-500" aria-hidden="true" data-id="element-139" data-mp="3qcnpa6ikm870" />
              💡 Top 3 personalized recommendations
            </p>
            <ol className="mt-4 space-y-3" data-id="element-140" data-mp="3vd1yvsztlq75">
              {recommendations.map((rec, index) => <li key={rec} className="flex gap-3 leading-relaxed text-ink-500" data-id="element-141" data-mp="3hdaffcqjbhlr" data-mpi={index}>
                  <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-orangey-500 font-mono text-[0.65rem] font-semibold text-white" data-id="element-142" data-mp="3mblnbhx9ri1c">
                    {index + 1}
                  </span>
                  {rec}
                </li>)}
            </ol>
          </div>
        </motion.div>

        <p className="mx-auto mt-7 max-w-2xl text-center font-display text-lg font-bold leading-snug text-white sm:text-xl" data-id="element-143" data-mp="1i2fx0ul0g47w">
          Not just numbers. A clear picture of where your dog is today — and what to focus on next.
        </p>

        <div className="mt-6 text-center" data-id="element-144" data-mp="145sywohbn80e">
          <CheckoutButton variant="onDark" className="w-full sm:w-auto" data-id="element-145" data-mp="146udh2xjqcmv">
            {CTA_LABEL}
          </CheckoutButton>
        </div>
      </div>
    </section>;
}